// Screenshot helpers for App Improvements.
//
// The server caps an image at ~1.5M characters and nginx's default body
// limit is 1MB, so everything leaves the browser as a JPEG no wider than
// MAX_WIDTH, re-compressed until it fits TARGET_CHARS.

const MAX_WIDTH = 1400;
const TARGET_CHARS = 850_000;

// Anything marked data-html2canvas-ignore (the feedback button, the modal)
// is left out of the picture.
export async function captureViewport() {
  // Loaded on demand: ~200KB that nobody needs until they click the button.
  const { default: html2canvas } = await import("html2canvas");
  const canvas = await html2canvas(document.body, {
    x: window.scrollX,
    y: window.scrollY,
    width: window.innerWidth,
    height: window.innerHeight,
    windowWidth: document.documentElement.clientWidth,
    windowHeight: window.innerHeight,
    scale: 1,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
  });
  return compressCanvas(canvas);
}

export function compressCanvas(source) {
  const ratio = Math.min(1, MAX_WIDTH / source.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(source.width * ratio);
  canvas.height = Math.round(source.height * ratio);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  let quality = 0.8;
  let url = canvas.toDataURL("image/jpeg", quality);
  while (url.length > TARGET_CHARS && quality > 0.3) {
    quality -= 0.15;
    url = canvas.toDataURL("image/jpeg", quality);
  }
  return url;
}

// A pasted or uploaded image file, run through the same size limits.
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      URL.revokeObjectURL(objectUrl);
      resolve(compressCanvas(canvas));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("That file isn't an image we can read."));
    };
    img.src = objectUrl;
  });
}

// The first image on a paste event's clipboard, or null.
export function imageFromPaste(event) {
  const items = event.clipboardData?.items || [];
  for (const item of items) {
    if (item.type.startsWith("image/")) return item.getAsFile();
  }
  return null;
}
